import { syncEpicEhrEncounterFromOrderGet } from '@/features/auth/epicRedoxContextStorage'
import ProviderAPIClient from '@/features/auth/ProviderAPIClient'
import type { CanonicalOrderPayload } from '@/ordering/canonicalOrder'
import type { ReanalysisDecision, ReanalysisSignals } from '@/types/OrderReportTypes'
import {
  AuthorizingProfessionalInfo,
  OrderFormInfoData,
  ParentFieldShape,
  PartialRequisitionFormData,
  PhysicianInfo,
  RequisitionFormData,
} from '@/types/RequisitionFormTypes'
import { FormCompletionChecker } from './FormCompletionChecker'

/**
 * Thrown when the backend rejects a parent's MRN because it is already in use
 * by another patient in the same organization (HTTP 409, GEN-3010).
 *
 * Caught by ``OrderInProgress`` so the user sees the backend's specific message
 * inline (Mother/Father card area) instead of the generic "Failed to update
 * order" banner. Marked with a discriminator so existing ``Sentry`` /
 * generic-error handling can branch on it without dynamic message parsing.
 */
export const MRN_CONFLICT_ERROR_CODE = 'MRN_CONFLICT' as const

export class MrnConflictError extends Error {
  public readonly code = MRN_CONFLICT_ERROR_CODE

  constructor(message: string) {
    super(message)
    this.name = 'MrnConflictError'
  }
}

/**
 * provider-api rejected the order update with HTTP 422 and a PHI-free `detail`
 * (GEN-5654: e.g. a comparator contact is required). Surfaced inline by
 * ``OrderInProgress`` instead of the generic failure banner.
 */
export const ORDER_REJECTED_ERROR_CODE = 'ORDER_REJECTED' as const
const ORDER_REJECTED_FALLBACK_MESSAGE = 'The order could not be updated. Check the order details and try again.'

export class OrderRejectedError extends Error {
  public readonly code = ORDER_REJECTED_ERROR_CODE

  constructor(message: string) {
    super(message)
    this.name = 'OrderRejectedError'
  }
}

/** Code-based (HMR-safe) detector, same rationale as ``isMrnConflictError``. */
export function isOrderRejectedError(error: unknown): error is OrderRejectedError {
  if (error == null || typeof error !== 'object') return false
  return (error as { code?: unknown }).code === ORDER_REJECTED_ERROR_CODE
}

/**
 * Non-404, non-OK response from ``getOrderById``. Carries the HTTP status so
 * callers can branch (e.g. 401 → re-login) without parsing the message.
 */
export class OrderFetchError extends Error {
  public readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'OrderFetchError'
    this.status = status
  }
}

/** Only a plain string `detail` is shown; structured field errors may echo input values. */
async function buildOrderRejectedError(response: Response): Promise<OrderRejectedError> {
  try {
    const parsed = JSON.parse(await response.text()) as { detail?: unknown }
    if (typeof parsed.detail === 'string' && parsed.detail.trim()) {
      return new OrderRejectedError(parsed.detail.trim())
    }
  } catch {
    /* fall through to the generic message */
  }
  return new OrderRejectedError(ORDER_REJECTED_FALLBACK_MESSAGE)
}

/**
 * HMR-safe MRN-conflict detector. ``instanceof`` is unreliable in dev when
 * Vite hot-reloads ``DatabaseOperations.ts`` independently of a consumer
 * module: two distinct ``MrnConflictError`` class objects end up in memory
 * and ``instanceof`` returns false even though the runtime values match.
 * Duck-typing on the ``code`` discriminator avoids that foot-gun.
 */
export function isMrnConflictError(error: unknown): error is MrnConflictError {
  if (error == null || typeof error !== 'object') return false
  const codeProp = (error as { code?: unknown }).code
  const nameProp = (error as { name?: unknown }).name
  return codeProp === MRN_CONFLICT_ERROR_CODE || nameProp === 'MrnConflictError'
}

/**
 * Versioned IMDB json_payload shape.
 *
 * Versioning policy:
 *  - payloadVersion 1 (default / absent): legacy TRF shape only — `orderFormInfo` + `requisitionFormData`.
 *  - payloadVersion 2: canonical-first — `canonicalOrder` is the source of truth; `requisitionFormData`
 *    is retained for read-compat with code that has not yet been migrated to the canonical path.
 *
 * Read rules: prefer `canonicalOrder` when present; fall back to `requisitionFormData` for v1 rows.
 */
export interface ExistingRequestPayload {
  /** Legacy TRF header data — present in all rows; used as fallback when canonicalOrder is absent. */
  orderFormInfo: OrderFormInfoData
  /** Legacy TRF body — present in all rows for read-compat with un-migrated consumers. */
  requisitionFormData: PartialRequisitionFormData
  /** Canonical order snapshot (v2+). When present, this is authoritative; requisitionFormData is secondary. */
  canonicalOrder?: CanonicalOrderPayload | null
  /** Indicates json_payload schema version. Absent = 1 (legacy). */
  payloadVersion?: number
}

/**
 * Filter out empty parent data from requisition form data.
 * Parent data is excluded if the user has not started filling it.
 */
function filterEmptyParentData<T extends PartialRequisitionFormData | RequisitionFormData>(formData: T): T {
  const { mother, father, ...rest } = formData

  const filteredData = { ...rest } as T

  // Only include mother data if user has started filling it
  if (mother && FormCompletionChecker.hasParentDataStarted(mother as ParentFieldShape)) {
    filteredData.mother = mother
  }

  // Only include father data if user has started filling it
  if (father && FormCompletionChecker.hasParentDataStarted(father as ParentFieldShape)) {
    filteredData.father = father
  }

  return filteredData
}

/** Options for POST/PUT json_payload on tracked orders (legacy `/database/orders` path). */
export type TrackedOrderPersistOptions = {
  canonicalOrder?: CanonicalOrderPayload | null
  /**
   * Effective consent flow (UPPER_CASE), same resolution as ordering intent `tests[].consentFlowId`.
   * Backfills `requisitionFormData.consentFlowId` when missing so IMDB normalization / PM consent steps see it.
   */
  consentFlowId?: string | null
  /**
   * Epic SMART access token for provider-api Redox parent ServiceRequest write-back on
   * `PUT /database/orders/{id}` (with `ehr_encounter_id`). Omit outside Epic flow.
   */
  access_token?: string | null
  /** Epic Encounter FHIR id; required with `access_token` for Redox write-back on order update. */
  ehr_encounter_id?: string | null
  /**
   * Epic Contact Serial Number for the active encounter — fallback Redox encounter resolution
   * when `ehr_encounter_id` is unavailable (cross-window edits, cleared localStorage). The
   * server uses `Encounter?identifier=<csn_system>|<csn>` with the SMART `access_token` to
   * recover the FHIR encounter id; never persisted on the order row.
   */
  encounter_csn?: string | null
  /**
   * GEN-XXXX: raw "what the user changed" facts for the reanalysis decision. The
   * SPA no longer decides whether an edit triggers a reanalysis — it reports
   * these two booleans and provider-api owns the decision + enforcement
   * (eligibility, in-flight / per-order cap). This is the SPA's internal option
   * grouping; on the wire the two facts are serialized as TOP-LEVEL
   * ``clinicalInformationModified`` / ``comparatorAdded`` fields on
   * ``OrderUpdate`` (NOT nested — the backend schema reads them top-level, and a
   * nested object is silently dropped). Omitted for a normal edit that reports
   * neither a clinical change nor a comparator add. The former spoofable
   * ``?reanalysis`` URL flag is NOT sent — authorization is derived server-side
   * from the actor's ``order:submit`` permission and the order's state.
   */
  reanalysisSignals?: ReanalysisSignals | null
  /**
   * Optional audit note describing why reanalysis was requested. Sent as the
   * top-level ``requestReason`` on ``OrderUpdate`` and only meaningful alongside
   * reanalysis signals that warrant one. Max 500 chars (enforced server-side).
   */
  reanalysisRequestReason?: string | null
}

function withResolvedConsentFlowOnRequisition<T extends PartialRequisitionFormData | RequisitionFormData>(
  filteredFormData: T,
  consentFlowId?: string | null
): T {
  const existing = typeof filteredFormData.consentFlowId === 'string' ? filteredFormData.consentFlowId.trim() : ''
  if (existing) return filteredFormData
  const resolved = consentFlowId?.trim()
  if (!resolved) return filteredFormData
  return { ...filteredFormData, consentFlowId: resolved }
}

/** Build a user-visible message from a failed provider-api response (e.g. FastAPI `detail`). */
async function messageFromFailedResponse(response: Response, fallback: string): Promise<string> {
  const statusSuffix = ` (HTTP ${response.status})`
  try {
    const text = await response.text()
    if (!text?.trim()) {
      return `${fallback}${statusSuffix}`
    }
    try {
      const parsed = JSON.parse(text) as { detail?: unknown }
      const { detail } = parsed
      if (typeof detail === 'string' && detail.trim()) {
        return `${detail.trim()}${statusSuffix}`
      }
      if (Array.isArray(detail) && detail.length > 0) {
        const parts = detail.map((item: { msg?: string }) =>
          item && typeof item.msg === 'string' ? item.msg : JSON.stringify(item)
        )
        return `${parts.join('; ')}${statusSuffix}`
      }
    } catch {
      const trimmed = text.trim().slice(0, 300)
      if (trimmed) {
        return `${trimmed}${statusSuffix}`
      }
    }
  } catch {
    // ignore
  }
  return `${fallback}${statusSuffix}`
}

export enum OrderStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  SUBMITTED = 'submitted',
  ACCESSIONED = 'accessioned',
  COMPLETE = 'complete',
  CANCELLED = 'cancelled',
  REANALYSIS = 'reanalysis',
}

interface TrackedOrderFormState {
  status: OrderStatus
  id?: string
  provider_id?: string
  patient_id?: string
  json_payload?: ExistingRequestPayload
  /** ISO-8601 with offset from provider-api (`timestamptz`); never a `Date` on the wire. */
  created_at?: string | null
  /** ISO-8601 with offset from provider-api (`timestamptz`); never a `Date` on the wire. */
  updated_at?: string | null
  /**
   * Server-owned instant the order's current TRF was last sent (GEN-5494): at
   * submit, and on any placed-order edit whose refreshed TRF the materiality
   * gate classifies as material. ISO-8601 with offset. Null on legacy rows the
   * backfill has not reached yet; the in-progress banner falls back to
   * `created_at` in that window. Never the viewer's current local time.
   */
  trf_sent_at?: string | null
  accessioned_on?: string | null
  /** Inflection order external id from source_object_identifier when present (id-gen / TEMP). */
  inflection_external_id?: string | null
  /**
   * SeqOne HPO lock flag from the authoritative order row (provider-api
   * OrderResponse). When true, HPO terms on existing/persisted members are
   * frozen (inflection_test). Null/undefined => not locked. Surfaced so the SPA
   * reflects the backend lock rather than the (possibly stale) canonicalOrder blob.
   */
  seqone_hpo_lock?: boolean | null
  /**
   * Short-lived pre-signed S3 GET URL for the TRF PDF provider-api rendered for
   * this order (GEN-4162). Null for orders that were never placed and for legacy
   * orders whose canonical document can no longer be reconstructed server-side —
   * in that case there is no server artifact to show and the caller falls back to
   * client-side generation.
   */
  trf_pdf_url?: string | null
  /** Lifetime of `trf_pdf_url` in seconds; the order must be re-fetched to get a fresh URL. */
  trf_pdf_expires_in?: number | null
  /** Which artifact `trf_pdf_url` points at: the countersigned TRF or the form as placed. */
  trf_pdf_doc_type?: TrfPdfDocType | null
  /**
   * Status of the signature request this order is currently waiting on (GEN-4030).
   *
   * Null/undefined means **no request is outstanding** — none was ever sent (the
   * organization may not have e-signature enabled, or the order may predate it), or
   * the last one was canceled without a replacement. That is deliberately distinct
   * from unsigned, which is a concrete status other than `signed`.
   *
   * Only the order's *current* request is reported. A replaced request keeps its own
   * status server-side but no longer speaks for the order, so it never appears here.
   */
  trf_signature_status?: TrfSignatureStatus | null
  /**
   * The signature request the current status came from, namespaced by vendor so ids
   * from different vendors cannot collide. Changes when an edited order supersedes its
   * outstanding request and a replacement is sent.
   */
  trf_signature_external_document_id?: string | null
  /** Which e-signature vendor produced this request, e.g. `docusign`. */
  trf_signature_vendor?: string | null
  /** When the vendor reported the current status — its timestamp for the transition, not ours. */
  trf_signature_updated_at?: string | null
  /**
   * How this request reaches its signer. Null when the backend could not read it,
   * which reads as `email` — the channel every order used before the embedded one
   * existed.
   */
  trf_signature_channel?: TrfSignatureChannel | null
  /**
   * What the signer typed when they declined this TRF. Null when the status is not
   * `declined`, when they submitted the decline without a reason, or when the backend
   * did not report one. The order page says so in that last case rather than looking
   * empty.
   */
  trf_signature_decline_reason?: string | null
  /** When the order was cancelled (ISO). Null unless `status` is `cancelled`. */
  cancelled_at?: string | null
  /** Display name of the user who cancelled. Null for cancellations recorded before attribution. */
  cancelled_by_name?: string | null
}

/**
 * Status of an order's TRF signature request, in the vendor-neutral vocabulary
 * provider-api translates every vendor's own statuses into.
 *
 * `signed` is the terminus we care about. A vendor may distinguish finer states on the
 * way there (DocuSign, for one, reports `signed` per recipient and `completed` once
 * every recipient has signed); the TRF has a single signer, and the backend collapses
 * that to `signed` before the SPA ever sees it.
 *
 * There is no `superseded` member: a replaced request keeps the status it actually
 * reached, and being replaced is recorded server-side as a timestamp rather than as a
 * status. The SPA only ever sees the request that currently speaks for the order.
 */
export type TrfSignatureStatus = 'created' | 'sent' | 'delivered' | 'signed' | 'declined' | 'canceled' | 'expired'

/**
 * How a signature request reaches its signer.
 *
 * `email` is the original flow: the vendor mails the signer a link to its own hosted
 * signing page. `embedded` mails nothing at all — the request is addressed to a captive
 * recipient and the clinician signs in-app.
 *
 * This is not cosmetic. The same status means different things on the two channels, so
 * anything describing a status to a person needs the channel to describe it truthfully.
 */
export type TrfSignatureChannel = 'email' | 'embedded'

/**
 * The TRF artifact a pre-signed URL points at. `signed` is the countersigned
 * document and is preferred over `submitted` when it exists; they are separate
 * lifecycle documents, not two names for the same file.
 */
export type TrfPdfDocType = 'signed' | 'submitted'

/** A usable pre-signed link to an order's server-rendered TRF PDF. */
export interface OrderTrfPdfLink {
  url: string
  expiresIn: number | null
  docType: TrfPdfDocType | null
}

/**
 * Pre-signed TRF link from an order-details response, or null when the order has
 * no server-rendered TRF (see `trf_pdf_url`).
 */
export function orderTrfPdfLinkFrom(
  order:
    | {
        trf_pdf_url?: string | null
        trf_pdf_expires_in?: number | null
        trf_pdf_doc_type?: string | null
      }
    | null
    | undefined
): OrderTrfPdfLink | null {
  const url = order?.trf_pdf_url?.trim()
  if (!url) return null
  const docType = order?.trf_pdf_doc_type?.trim()
  return {
    url,
    expiresIn: typeof order?.trf_pdf_expires_in === 'number' ? order.trf_pdf_expires_in : null,
    docType: docType === 'signed' || docType === 'submitted' ? docType : null,
  }
}

/** Trimmed Inflection external id for display under patient MRN when present. */
export function inflectionExternalIdFromOrder(
  order: { inflection_external_id?: string | null } | null | undefined
): string | null {
  const value = order?.inflection_external_id?.trim()
  return value || null
}

/** Optional fields for POST .../orders/{id}/submit (body + query; backend OrderSubmitRequest + submit_via_redox). */
export interface OrderSubmitExtras {
  access_token?: string | null
  ehr_encounter_id?: string | null
  /**
   * Query param. Opt in to Redox write-back — set only when the launch is Epic/EHR **and**
   * a concrete `ehr_encounter_id` was chosen, since the backend needs both the encounter and
   * the token to write back. Sending it for an Epic submit with no encounter asks for a
   * write-back the server cannot perform.
   */
  submit_via_redox?: boolean
}

/** Nested `order` on submit — matches backend OrderUpdate. */
interface OrderUpdateSubmitBody {
  provider_id?: string
  patient_id?: string
  json_payload?: ExistingRequestPayload
  status?: string
}

interface OrderSubmitRequestBody {
  order: OrderUpdateSubmitBody
  access_token?: string
  ehr_encounter_id?: string
}

export interface OrderComparatorSummary {
  patient_id: string
  relationship: string
  first_name?: string | null
  last_name?: string | null
  date_of_birth?: string | null
  sex?: string | null
  /**
   * Organization-scoped medical record number for this comparator/guardian.
   * Populated by the org-scoped orders list (submitted orders) and by the
   * ordering-session drafts projection; null when the member has no MRN in the
   * caller's org.
   */
  mrn?: string | null
  /** True when IMDB marks this member as primary guardian (may still be a sequencing comparator). */
  is_primary_guardian?: boolean
  /** True only when this member is a sequencing comparator (lab samples), not contact-only. */
  testing_comparator?: boolean
}

export interface OrderStatusResponse {
  order_id: string
  status: string
  accessioned?: boolean | null
  accessioned_on?: string | null
  updated_at?: string | null
  created_at?: string | null
  external_case_key?: string | null
  /** Inflection order external id from source_object_identifier when present (id-gen / TEMP). */
  inflection_external_id?: string | null
  // GEN-1277: Patient information
  patient_id?: string | null
  patient_first_name?: string | null
  patient_last_name?: string | null
  patient_date_of_birth?: string | null
  patient_sex?: string | null
  patient_mrn?: string | null
  // GEN-1277: Provider information
  provider_first_name?: string | null
  provider_last_name?: string | null
  // GEN-5106: distinct authorizing (responsible) provider; present only when different from the
  // ordering provider (who populates provider_first_name/provider_last_name). Null otherwise.
  authorizing_provider_first_name?: string | null
  authorizing_provider_last_name?: string | null
  /** IMDB catalog test name(s) for this order's lab line(s), comma-separated when multiple */
  sequencing_name?: string | null
  /** Biological comparators (e.g. parents) when present on the family order */
  comparators?: OrderComparatorSummary[] | null
  /**
   * When set, the row is a config-driven ordering session draft (no IMDB order yet).
   * `order_id` is the session UUID; resume with `/order/start?sessionId=…`.
   */
  ordering_session_id?: string | null
}

export enum ReportStatusEnum {
  PENDING = 'pending',
  GENERATED = 'generated',
  REVIEWED = 'reviewed',
  DELIVERED = 'delivered',
}

export interface ReportResponse {
  id: string
  analysis_result_id: string
  report_type: string
  status: ReportStatusEnum
  generated_at?: string | null
  reviewed_at?: string | null
  delivered_at?: string | null
  report_data?: Record<string, unknown> | null
  pdf_path?: string | null
  reviewer_name?: string | null
  created_at?: string | null
  updated_at?: string | null
}

export async function createOrder(
  data: OrderFormInfoData,
  formData: PartialRequisitionFormData,
  status: OrderStatus,
  caller: string,
  options?: TrackedOrderPersistOptions
): Promise<TrackedOrderFormState> {
  // Filter out empty parent data from the payload
  const filteredFormData = withResolvedConsentFlowOnRequisition(filterEmptyParentData(formData), options?.consentFlowId)

  const formPayload: TrackedOrderFormState = {
    status,
    json_payload: {
      orderFormInfo: data,
      requisitionFormData: filteredFormData,
      ...(options?.canonicalOrder && {
        canonicalOrder: options.canonicalOrder,
        payloadVersion: 2,
      }),
    },
    provider_id: data.orderingProvider?.id,
    patient_id: data.patient.id,
  }

  const client = ProviderAPIClient.getInstance()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  const response = await client.post(`/database/orders?caller=${encodeURIComponent(caller)}`, formPayload, headers)
  return response.json()
}

/**
 * Fetch the full order for a patient.
 *
 * When `epicAccessToken` is provided, it is sent as the `X-EHR-Access-Token`
 * header and the server treats `patientId` as the Epic FHIR Patient logical id:
 * it reads `Patient/{patientId}` from the tenant's Epic FHIR server, derives the
 * MRN, and resolves the order by MRN + the caller's organization (org is taken
 * from the JWT). It falls back to the proband-external-id lookup if the token is
 * absent, the FHIR read fails, no MRN is found, or the MRN+org lookup finds
 * nothing. Mirrors {@link getOrderStatusByPatient} so both lookups resolve the
 * same order during the EHR / SMART-on-FHIR launch flow.
 *
 * @param patientId - Proband external id, or (with a token) the Epic FHIR Patient logical id
 * @param caller - Audit label; defaults server-side to `provider-api`
 * @param epicAccessToken - Optional Epic access token for FHIR-derived MRN resolution
 */
export async function getOrderByPatient(
  patientId: string,
  caller: string,
  epicAccessToken?: string | null
): Promise<TrackedOrderFormState> {
  const client = ProviderAPIClient.getInstance()
  const headers = epicAccessToken ? { 'X-EHR-Access-Token': epicAccessToken } : undefined
  const response = await client.get(
    `/database/orders/patient/${patientId}?caller=${encodeURIComponent(caller)}`,
    headers
  )

  if (response.ok) {
    const body = await response.json()
    if (body.present) {
      syncEpicEhrEncounterFromOrderGet(body.value)
      return body.value
    }
  }
  return {
    status: OrderStatus.DRAFT,
  }
}

export type WorkspaceSortColumn = 'case' | 'patient' | 'dob' | 'provider' | 'orderDate' | 'lastUpdate' | 'status'

export interface WorkspaceSearchQuery {
  /** PHI (names, MRNs): sent in the POST body only, never in the URL. */
  search: string
  /** Raw order statuses; empty = no status filter. */
  statuses: string[]
  sort: WorkspaceSortColumn
  dir: 'asc' | 'desc'
  page: number
  page_size: number
}

export interface WorkspaceSearchResult {
  /** IMDB orders and open ordering-session drafts (`status: 'draft'`, `ordering_session_id` set). */
  items: OrderStatusResponse[]
  total: number
  page: number
  page_size: number
  /** Rows per lower-cased raw status over the search-filtered set (status filter not applied). */
  status_counts: Record<string, number>
}

/**
 * One server-side page of the Workspace (orders + open drafts). The server picks the scope:
 * org-wide for org_admin, otherwise the caller's (or delegating clinician's) orders and drafts.
 */
export async function searchWorkspaceOrders(
  query: WorkspaceSearchQuery,
  onBehalfOfUserId?: string | null
): Promise<WorkspaceSearchResult> {
  const client = ProviderAPIClient.getInstance()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (onBehalfOfUserId) {
    headers['X-On-Behalf-Of'] = onBehalfOfUserId
  }
  const response = await client.post('/workspace/orders/search', query, headers)
  if (!response.ok) {
    throw new Error(`Failed to load workspace orders: ${response.statusText}`)
  }
  return response.json()
}

export async function getOrderById(orderId: string, caller: string): Promise<TrackedOrderFormState | null> {
  const client = ProviderAPIClient.getInstance()
  const response = await client.get(`/database/orders/${orderId}?caller=${encodeURIComponent(caller)}`)

  if (response.ok) {
    const order = await response.json()
    syncEpicEhrEncounterFromOrderGet(order)
    return order
  }
  if (response.status === 404) {
    return null
  }
  throw new OrderFetchError(response.status, `Failed to get order: ${response.statusText}`)
}

/**
 * The signature-request fields the order-details response projects (GEN-4030),
 * isolated so the signature poller (GEN-5027) can consume them without the rest
 * of the order payload's shape.
 */
export interface OrderTrfSignatureFields {
  trf_signature_status?: TrfSignatureStatus | null
  trf_signature_external_document_id?: string | null
  trf_signature_vendor?: string | null
  trf_signature_updated_at?: string | null
  trf_signature_channel?: TrfSignatureChannel | null
  trf_signature_decline_reason?: string | null
}

/**
 * Fetch only the current TRF signature state for an order (GEN-5027).
 *
 * The order page fetches its order once on load; this is the lighter read the
 * signature poller repeats on an interval so a status change (an envelope
 * reaching `sent`, or a `signed` after the clinician signs) reflects without a
 * manual refresh. It hits the same provider-api order-details endpoint — never a
 * signature vendor directly, which is what keeps this compatible with DocuSign's
 * no-polling rule (that rule targets `Envelopes:get`, not our own API) — and
 * reads back only the projected `trf_signature_*` fields.
 *
 * Deliberately NOT `getOrderById`: that runs `syncEpicEhrEncounterFromOrderGet`
 * and returns the full tracked-order payload, neither of which a poll wants to
 * re-apply every few seconds. A 404 returns null (the order is gone) so the
 * poller can stop rather than retry a request that will never succeed.
 */
export async function getOrderTrfSignature(orderId: string, caller: string): Promise<OrderTrfSignatureFields | null> {
  const client = ProviderAPIClient.getInstance()
  const response = await client.get(`/database/orders/${orderId}?caller=${encodeURIComponent(caller)}`)

  if (response.ok) {
    const order = (await response.json()) as OrderTrfSignatureFields
    return {
      trf_signature_status: order?.trf_signature_status ?? null,
      trf_signature_external_document_id: order?.trf_signature_external_document_id ?? null,
      trf_signature_vendor: order?.trf_signature_vendor ?? null,
      trf_signature_updated_at: order?.trf_signature_updated_at ?? null,
      trf_signature_channel: order?.trf_signature_channel ?? null,
      trf_signature_decline_reason: order?.trf_signature_decline_reason ?? null,
    }
  }
  if (response.status === 404) {
    return null
  }
  throw new Error(`Failed to get order signature status: ${response.statusText}`)
}

/**
 * The patient identity fields required to render the signing page header.
 *
 * Deliberately a narrow projection so the call site never receives more
 * demographics than it displays: first + middle + last name, MRN, and birth
 * date (ISO string as stored in json_payload — consumers pass it directly to
 * the `BirthDateInput`-typed format helpers that accept strings).
 */
export interface OrderPatientHeaderFields {
  firstName: string
  middleName?: string | null
  lastName: string
  mrn: string
  birthDate: string | null
}

/**
 * Fetch only the patient identity fields needed to display the signing page header.
 *
 * Deliberately NOT `getOrderById`: that runs `syncEpicEhrEncounterFromOrderGet`
 * on every call. A patient-header lookup must never trigger an Epic EHR
 * encounter re-sync as a side effect of rendering a name.
 *
 * Hits the same endpoint as `getOrderById` but reads back only the five
 * fields from `json_payload.orderFormInfo.patient` — nothing else is
 * destructured, nothing is logged. A 404, an absent patient block, or a
 * patient with no name, MRN, or birth date returns null so the caller can
 * collapse to the anonymous-header variant. A missing last name alone does
 * not: first name, MRN, or date of birth are still identity the header shows.
 */
export async function getOrderPatientHeader(orderId: string, caller: string): Promise<OrderPatientHeaderFields | null> {
  const client = ProviderAPIClient.getInstance()
  const response = await client.get(`/database/orders/${orderId}?caller=${encodeURIComponent(caller)}`)

  if (response.ok) {
    const raw = (await response.json()) as {
      json_payload?: {
        orderFormInfo?: {
          patient?: {
            name?: { firstName?: string; middleName?: string; lastName?: string }
            mrn?: string
            birthDate?: string | null
          }
        }
      }
    }
    const patient = raw?.json_payload?.orderFormInfo?.patient
    const firstName = patient?.name?.firstName ?? ''
    const middleName = patient?.name?.middleName ?? null
    const lastName = patient?.name?.lastName ?? ''
    const mrn = patient?.mrn ?? ''
    const birthDate = patient?.birthDate ?? null
    if (!patient || !(firstName || middleName || lastName || mrn || birthDate)) return null
    return {
      firstName,
      middleName,
      lastName,
      mrn,
      birthDate,
    }
  }
  if (response.status === 404) {
    return null
  }
  throw new Error(`Failed to get order patient header: ${response.statusText}`)
}

/**
 * What an order update tells the caller about the TRF signature side effects it
 * triggered (GEN-5027). Today that is only whether a replacement DocuSign envelope
 * was requested, so the order page knows to poll for the asynchronous document
 * swap; kept as an object so future signature-side outcomes can be added without
 * touching every call site.
 */
export interface UpdateOrderResult {
  /**
   * True when the edit materially changed the TRF and provider-api published a
   * replacement envelope. The reissue is async and status-invisible (the order
   * keeps projecting the superseded envelope's `sent` status until the Lambda
   * swaps the document), so the page uses this to start polling for the new
   * `external_document_id` rather than treating the still-`sent` status as final.
   */
  trfSignatureReissueRequested: boolean
  /**
   * The order's TRF "sent" instant after this update (GEN-5494), read from the
   * PUT response as `trf_sent_at ?? created_at ?? null`. A material edit stamps a
   * fresh `trf_sent_at`; an immaterial/degraded edit leaves it unchanged, so the
   * response may echo the prior value or, on a body lacking both, null. A null
   * means the response carried no usable time and must not overwrite an
   * already-shown banner time.
   */
  trfSentAt: string | null
  /**
   * The authoritative reanalysis decision provider-api made for this committed
   * update, or ``null`` when the response omitted it (e.g. a normal edit that
   * sent no reanalysis signals, or a legacy backend). The page uses
   * ``reanalysis_triggered`` to drive the post-send read-only redirect instead
   * of its former locally-computed ``effectiveReanalysis``.
   */
  reanalysisDecision: ReanalysisDecision | null
}

export async function updateOrder(
  orderId: string,
  data: OrderFormInfoData,
  formData: PartialRequisitionFormData,
  orderStatus: OrderStatus,
  caller: string,
  options?: TrackedOrderPersistOptions
): Promise<UpdateOrderResult> {
  // Filter out empty parent data from the payload
  const filteredFormData = withResolvedConsentFlowOnRequisition(filterEmptyParentData(formData), options?.consentFlowId)

  const orderPayload: TrackedOrderFormState & {
    access_token?: string
    ehr_encounter_id?: string
    encounter_csn?: string
    clinicalInformationModified?: boolean
    comparatorAdded?: boolean
    requestReason?: string
  } = {
    status: orderStatus,
    json_payload: {
      orderFormInfo: data,
      requisitionFormData: filteredFormData,
      ...(options?.canonicalOrder && {
        canonicalOrder: options.canonicalOrder,
        payloadVersion: 2,
      }),
    },
    provider_id: data.orderingProvider?.id,
    patient_id: data.patient.id,
  }

  const token = options?.access_token?.trim()
  if (token) {
    orderPayload.access_token = token
  }
  const encounterId = options?.ehr_encounter_id?.trim()
  if (encounterId) {
    orderPayload.ehr_encounter_id = encounterId
  }
  const encounterCsn = options?.encounter_csn?.trim()
  if (encounterCsn) {
    orderPayload.encounter_csn = encounterCsn
  }
  // Reanalysis decision signals: forward the raw change facts to provider-api,
  // which owns the decision + enforcement. The backend's OrderUpdate schema
  // reads these as TOP-LEVEL fields (NOT nested under `reanalysisSignals` — a
  // nested object is silently dropped by Pydantic, defaulting both to false and
  // producing `reanalysis_decision: null` with no reanalysis). This matches the
  // flat body the preview endpoint already sends. Only attach the fields when the
  // SPA has something to report (either fact true) so a plain edit leaves them
  // unset. requestReason is only meaningful alongside the signals.
  const signals = options?.reanalysisSignals
  if (signals && (signals.clinicalInformationModified || signals.comparatorAdded)) {
    orderPayload.clinicalInformationModified = signals.clinicalInformationModified
    orderPayload.comparatorAdded = signals.comparatorAdded
    const reason = options?.reanalysisRequestReason?.trim()
    if (reason) {
      orderPayload.requestReason = reason
    }
  }

  const client = ProviderAPIClient.getInstance()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  const response = await client.put(
    `/database/orders/${orderId}?caller=${encodeURIComponent(caller)}`,
    orderPayload,
    headers
  )
  if (response.status === 409) {
    throw await buildOrderConflictError(response)
  }
  if (response.status === 422) {
    throw await buildOrderRejectedError(response)
  }
  // Any other non-ok status (e.g. 500) must throw so the caller's catch runs:
  // it surfaces the error inline, keeps the pending edit, skips the post-update
  // navigation (including the reanalysis redirect), and keeps values from the
  // rejected response out of UI state (banner time, reissue watch, signing hand-off).
  if (!response.ok) {
    throw new Error(await messageFromFailedResponse(response, 'Failed to update order'))
  }
  const body = await readOrderUpdateResponseBody(response)
  return {
    trfSignatureReissueRequested: body?.trf_signature_reissue_requested === true,
    trfSentAt: body?.trf_sent_at ?? body?.created_at ?? null,
    reanalysisDecision: body?.reanalysis_decision ?? null,
  }
}

/**
 * Read the JSON body of a 2xx order-update response. A 204 or empty-body 2xx is
 * a success, not a failure, so an empty payload yields null rather than a
 * `response.json()` parse error (which the caller would surface as a false
 * "Failed to update order").
 */
async function readOrderUpdateResponseBody(response: Response): Promise<{
  trf_signature_reissue_requested?: boolean
  trf_sent_at?: string | null
  created_at?: string | null
  reanalysis_decision?: ReanalysisDecision | null
} | null> {
  const text = (await response.text()).trim()
  if (!text) return null
  return JSON.parse(text)
}

/**
 * Build the error for a 409 order-update conflict. Provider-api's only 409 on
 * this route is the MRN-in-use conflict, carried as a string `detail`; that maps
 * to an `MrnConflictError` so the page shows the server's message inline and
 * skips Sentry. A 409 with a missing or non-string detail is an unexpected
 * conflict, so it becomes a generic error — the page reports "Failed to update
 * order" rather than falsely claiming an MRN conflict.
 */
async function buildOrderConflictError(response: Response): Promise<Error> {
  let mrnDetail: string | null = null
  try {
    const body = (await response.json()) as { detail?: unknown }
    if (body && typeof body.detail === 'string' && body.detail.trim()) {
      mrnDetail = body.detail
    }
  } catch {
    // Body wasn't JSON or the response stream was already consumed.
  }
  if (mrnDetail != null) {
    return new MrnConflictError(mrnDetail)
  }
  return new Error(`Failed to update order (HTTP ${response.status})`)
}

export async function submitOrder(
  orderId: string,
  data: OrderFormInfoData,
  formData: PartialRequisitionFormData | RequisitionFormData,
  caller: string,
  extras?: OrderSubmitExtras,
  options?: TrackedOrderPersistOptions
): Promise<void> {
  // Filter out empty parent data from the payload
  const filteredFormData = withResolvedConsentFlowOnRequisition(filterEmptyParentData(formData), options?.consentFlowId)

  const order: OrderUpdateSubmitBody = {
    status: OrderStatus.SUBMITTED,
    json_payload: {
      orderFormInfo: data,
      requisitionFormData: filteredFormData,
      ...(options?.canonicalOrder && {
        canonicalOrder: options.canonicalOrder,
        payloadVersion: 2,
      }),
    },
    provider_id: data.orderingProvider?.id,
    patient_id: data.patient.id,
  }

  const submitBody: OrderSubmitRequestBody = { order }
  const token = extras?.access_token?.trim()
  if (token) {
    submitBody.access_token = token
  }
  const encounterId = extras?.ehr_encounter_id?.trim()
  if (encounterId) {
    submitBody.ehr_encounter_id = encounterId
  }

  const client = ProviderAPIClient.getInstance()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  const params = new URLSearchParams()
  params.set('caller', caller)
  if (extras?.submit_via_redox) {
    params.set('submit_via_redox', 'true')
  }

  const response = await client.post(`/database/orders/${orderId}/submit?${params.toString()}`, submitBody, headers)
  if (!response.ok) {
    throw new Error(await messageFromFailedResponse(response, 'Failed to submit order'))
  }
  return response.json()
}

/**
 * Cancel an order via ``DELETE /orders/{orderId}/cancel``. The backend flips the
 * order to ``cancelled`` and emits any CareBus events; the SPA only reflects the
 * result. Throws on a non-ok response with a stable message (no response-body
 * leak), matching the neighboring ``submitOrder`` error handling.
 *
 * Lab-agnostic Redox cancellation (opt-in): pass ``cancelViaRedox: true`` plus the
 * logged-in user's Epic SMART ``ehrAccessToken`` to have provider-api best-effort
 * revoke the order's Epic ServiceRequest via Redox after the cancel commits. This
 * mirrors the submit path (``submitViaRedox`` / ``ehrAccessToken``) and should only
 * be used when the order was submitted to Redox from an Epic session. The revoke is
 * best-effort and never changes the response: a missing token, non-Redox org, or
 * Redox failure still yields the cancelled order.
 *
 * Wire format: ``cancelViaRedox`` goes in the query string and the Epic token in the
 * ``X-EHR-Access-Token`` header (never a body or URL param) so it can't leak into
 * request logs. No JSON body is sent on this DELETE.
 */
export async function cancelOrder(
  orderId: string,
  caller: string,
  options?: { cancelViaRedox?: boolean; ehrAccessToken?: string | null }
): Promise<void> {
  const client = ProviderAPIClient.getInstance()
  const cancelViaRedox = options?.cancelViaRedox === true
  const ehrAccessToken = options?.ehrAccessToken?.trim() || null
  // Only opt in to the Redox revoke when we actually have a token to hand off;
  // provider-api silently skips the revoke without one, so sending the flag alone
  // would be a no-op.
  const useRedox = cancelViaRedox && Boolean(ehrAccessToken)
  const query = `?caller=${encodeURIComponent(caller)}${useRedox ? '&cancelViaRedox=true' : ''}`
  const headers = useRedox ? { 'X-EHR-Access-Token': ehrAccessToken as string } : undefined
  const response = await client.delete(`/orders/${orderId}/cancel${query}`, headers)
  if (!response.ok) {
    throw new Error(`Failed to cancel order (HTTP ${response.status})`)
  }
}

/** ``GET /orders/{orderId}/restart-eligibility`` (GEN-6020). Cancel details are set only when ``eligible``. */
export interface OrderRestartEligibility {
  eligible: boolean
  isLatestOrder: boolean
  existingDraftSessionId: string | null
  cancelledAt: string | null
  cancelledByName: string | null
}

/** Whether the Restart Order action applies to this order. Throws on a non-2xx response. */
export async function getOrderRestartEligibility(
  orderId: string,
  signal?: AbortSignal
): Promise<OrderRestartEligibility> {
  const client = ProviderAPIClient.getInstance()
  const response = await client.get(`/orders/${orderId}/restart-eligibility`, undefined, true, signal)
  if (!response.ok) {
    throw new Error(`Failed to get order restart eligibility (HTTP ${response.status})`)
  }
  return (await response.json()) as OrderRestartEligibility
}

/**
 * Strategy-aware clinical-field edit window from ``GET /orders/{orderId}/edit-window``.
 * The backend derives ``editable`` (RCIGM → order status; INFLECTION_TEST → SeqOne
 * analysis state); ``windowState`` is for display/observability. This throws on a
 * non-ok response so the caller can fail safe to read-only rather than treating an
 * error as "editable".
 */
export interface OrderEditWindowResponse {
  orderId: string
  windowState: 'OPEN' | 'QUEUED' | 'CLOSED'
  editable: boolean
  status: string | null
  analysisFound: boolean
  editableFields: string[]
}

export async function getOrderEditWindow(
  orderId: string,
  caller: string,
  signal?: AbortSignal
): Promise<OrderEditWindowResponse> {
  const client = ProviderAPIClient.getInstance()
  const response = await client.get(
    `/orders/${orderId}/edit-window?caller=${encodeURIComponent(caller)}`,
    undefined,
    true,
    signal
  )
  if (!response.ok) {
    throw new Error(`Failed to get order edit window (HTTP ${response.status})`)
  }
  return (await response.json()) as OrderEditWindowResponse
}

/**
 * Response for the ``GET /orders/{orderId}/editability`` provider-api endpoint.
 * For ``inflection_test`` (LSMC) orgs this is the authoritative editability
 * signal: provider-api proxies the mission-control (mc-api) editability endpoint
 * (which reads live SeqOne analysis status) and passes the answer through.
 * ``editable: true`` means the SPA may allow editing every patient + comparator
 * section and enable "Send Updates"; ``false`` locks them. ``reason`` explains
 * the decision; ``analysisId`` / ``interpretationStatus`` / ``state`` describe
 * the governing analysis (null when the order has no analysis yet).
 */
export interface OrderEditabilityResponse {
  orderId: string
  editable: boolean
  reason: string
  analysisId: string | null
  interpretationStatus: string | null
  state: string | null
}

/**
 * Call the ``GET /orders/{orderId}/editability`` provider-api endpoint, which
 * proxies mc-api (the source of truth for ``inflection_test`` orders). The SPA
 * always talks to provider-api (not mc-api directly) so the M2M credentials
 * stay server-side; provider-api owns the mc-api request and passes ``editable``
 * (plus context) through.
 *
 * Throws on a non-2xx response; callers (see ``useIsOrderEditable``) decide the
 * fail-safe behavior. Mirrors :func:`getOrderEditWindow`.
 */
export async function getOrderEditability(
  orderId: string,
  caller: string,
  signal?: AbortSignal
): Promise<OrderEditabilityResponse> {
  const client = ProviderAPIClient.getInstance()
  const response = await client.get(
    `/orders/${orderId}/editability?caller=${encodeURIComponent(caller)}`,
    undefined,
    true,
    signal
  )
  if (!response.ok) {
    throw new Error(`Failed to get order editability (HTTP ${response.status})`)
  }
  return (await response.json()) as OrderEditabilityResponse
}

/**
 * Result of ``GET /ordering/orders/{orderId}/comparators/{patientId}/sample-collected``.
 *
 * ``sampleCollected`` is the headline: ``(specimenPresent || beakerPresent) && redoxEnabled``.
 * The three sub-fields are diagnostic detail for tooltips / copy — e.g. distinguishing
 * "the org isn't Redox-integrated" (``redoxEnabled === false``) from "sample not yet
 * collected" (``redoxEnabled === true`` but neither specimen nor beaker present).
 */
export interface ComparatorSampleCollectedResponse {
  sampleCollected: boolean
  specimenPresent: boolean
  beakerPresent: boolean
  redoxEnabled: boolean
}

/**
 * Sentinel ``patientId`` that checks the order's primary patient (proband) rather than a
 * comparator. Case-insensitive server-side; the backend resolves the proband's real id, so
 * the UI never needs it. Pass this to {@link getComparatorSampleCollected} to gate proband-
 * scoped actions such as order cancellation.
 */
export const PROBAND_SAMPLE_COLLECTED_SENTINEL = 'proband'

/**
 * Whether a person on a *submitted* order has had their sample collected by the lab.
 *
 * ``patientId`` is either a comparator's IMDB patient/member id (``demographic.demographicId``)
 * or the {@link PROBAND_SAMPLE_COLLECTED_SENTINEL} (``"proband"``) to check the primary patient.
 * This describes a submitted order, so callers must not invoke it for drafts (no order id yet).
 *
 * Fails safe: a 404 (order not found / not in the caller's organization — intentionally
 * indistinguishable) returns ``null`` so the caller can decide how to handle a missing order.
 * Any other non-ok (422 bad path params, 401 unauthenticated, 5xx) throws, so a fail-closed
 * gate treats the unknown state as "still collected / blocked" rather than silently allowing
 * the action.
 */
export async function getComparatorSampleCollected(
  orderId: string,
  patientId: string,
  signal?: AbortSignal
): Promise<ComparatorSampleCollectedResponse | null> {
  const client = ProviderAPIClient.getInstance()
  const response = await client.get(
    `/ordering/orders/${encodeURIComponent(orderId)}/comparators/${encodeURIComponent(patientId)}/sample-collected`,
    undefined,
    true,
    signal
  )
  if (response.status === 404) {
    return null
  }
  if (!response.ok) {
    throw new Error(`Failed to get comparator sample-collected status (HTTP ${response.status})`)
  }
  return (await response.json()) as ComparatorSampleCollectedResponse
}

export async function updateHPOTerms(
  orderId: string,
  data: OrderFormInfoData,
  formData: PartialRequisitionFormData | RequisitionFormData,
  caller: string,
  options?: TrackedOrderPersistOptions
): Promise<UpdateOrderResult> {
  // Filter out empty parent data from the payload
  const filteredFormData = withResolvedConsentFlowOnRequisition(filterEmptyParentData(formData), options?.consentFlowId)

  const orderPayload: TrackedOrderFormState = {
    status: OrderStatus.SUBMITTED,
    json_payload: {
      orderFormInfo: data,
      requisitionFormData: filteredFormData,
      ...(options?.canonicalOrder && {
        canonicalOrder: options.canonicalOrder,
        payloadVersion: 2,
      }),
    },
    provider_id: data.orderingProvider?.id,
    patient_id: data.patient.id,
  }

  const client = ProviderAPIClient.getInstance()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  const response = await client.put(
    `/database/orders/${orderId}/hpo-update?caller=${encodeURIComponent(caller)}`,
    orderPayload,
    headers
  )
  if (response.status === 409) {
    throw await buildOrderConflictError(response)
  }
  // A non-2xx is a real failure: throw so the caller keeps the pending edit and
  // no value from the rejected response reaches UI state.
  if (!response.ok) {
    throw new Error(`Failed to update order (HTTP ${response.status})`)
  }
  // GEN-5494: the HPO-route update returns the same UpdateOrderResult shape as
  // `updateOrder`, so any caller can refresh the "sent" banner from the same
  // mapping (`trf_sent_at ?? created_at ?? null`) regardless of route.
  const body = await readOrderUpdateResponseBody(response)
  return {
    trfSignatureReissueRequested: body?.trf_signature_reissue_requested === true,
    trfSentAt: body?.trf_sent_at ?? body?.created_at ?? null,
    reanalysisDecision: body?.reanalysis_decision ?? null,
  }
}

/**
 * Resolve order status for a patient.
 *
 * When `epicAccessToken` is provided, it is sent as the `X-EHR-Access-Token`
 * header. In that case the server treats `patientId` as the Epic FHIR Patient
 * logical id, reads `Patient/{patientId}` from the tenant's Epic FHIR server,
 * derives the MRN, and resolves the order by MRN + the caller's organization
 * (org is taken from the JWT). If the token is absent, the FHIR read fails, no
 * MRN is found, or the MRN+org lookup finds nothing, the server falls back to
 * the proband-external-id lookup (the existing behavior). No org id, MRN, or
 * other query params are sent by the UI.
 *
 * @param patientId - Proband external id, or (with a token) the Epic FHIR Patient logical id
 * @param caller - Audit label; defaults server-side to `provider-api`
 * @param epicAccessToken - Optional Epic access token for FHIR-derived MRN resolution
 */
export async function getOrderStatusByPatient(
  patientId: string,
  caller: string,
  epicAccessToken?: string | null
): Promise<OrderStatusResponse | null> {
  const client = ProviderAPIClient.getInstance()
  const headers = epicAccessToken ? { 'X-EHR-Access-Token': epicAccessToken } : undefined
  const response = await client.get(
    `/database/orders/status/patient/${patientId}?caller=${encodeURIComponent(caller)}`,
    headers
  )

  if (!response.ok) {
    throw new Error(`Failed to get order status: ${response.statusText}`)
  }

  const body = await response.json()
  if (body.present) {
    return body.value
  }
  return null
}

export async function getOrderReport(orderId: string, caller: string): Promise<ReportResponse> {
  const client = ProviderAPIClient.getInstance()
  const response = await client.get(`/database/orders/${orderId}/report?caller=${encodeURIComponent(caller)}`)

  if (!response.ok) {
    throw new Error(`Failed to get order report: ${response.statusText}`)
  }

  return response.json()
}

/** Row from GET /database/organizations/{org_id}/practitioners (IMDB organization_practitioner_reln). */
export interface OrganizationPractitionerEntry {
  organization_practitioner_reln_id: string
  practitioner_id: string
  npi?: string | null
  email?: string | null
  phone?: string | null
  /** Provider role title / credential (e.g. "MD"). Surfaced as roleTitle on the order/TRF. */
  medical_degree?: string | null
  data?: Record<string, unknown>
}

export function organizationPractitionerEntryNpi(entry: OrganizationPractitionerEntry): string {
  const top = entry.npi?.trim()
  if (top) return top
  const d = entry.data
  if (d && typeof d.npi === 'string' && d.npi.trim()) return d.npi.trim()
  return ''
}

/**
 * Resolve the provider's role title / credential (e.g. "MD", "DO") for a database
 * (non-EHR) practitioner. Prefers the top-level `medical_degree` column, falling back
 * to legacy role fields on the data blob. Returns `''` when none is present — the value
 * is config/data-driven free-text and is frequently absent, so callers must render it
 * defensively (hide when empty).
 */
export function organizationPractitionerRoleTitle(entry: OrganizationPractitionerEntry): string {
  const d = (entry.data ?? {}) as Record<string, unknown>
  return (
    entry.medical_degree ?? String(d.role_title ?? d.roleTitle ?? d.medical_degree ?? d.medicalDegree ?? d.role ?? '')
  ).trim()
}

export function filterOrganizationPractitionersWithNpi(
  entries: OrganizationPractitionerEntry[]
): OrganizationPractitionerEntry[] {
  return entries.filter((e) => organizationPractitionerEntryNpi(e).length > 0)
}

/** Dedupe when IMDB returns multiple reln rows per practitioner. */
export function dedupeOrganizationPractitionersByPractitionerId(
  entries: OrganizationPractitionerEntry[]
): OrganizationPractitionerEntry[] {
  const seen = new Set<string>()
  const out: OrganizationPractitionerEntry[] = []
  for (const e of entries) {
    if (seen.has(e.practitioner_id)) continue
    seen.add(e.practitioner_id)
    out.push(e)
  }
  return out
}

export function organizationPractitionerDisplayName(entry: OrganizationPractitionerEntry): string {
  const d = entry.data
  if (d && typeof d === 'object') {
    const full = d.full_name ?? d.name ?? d.display_name
    if (typeof full === 'string' && full.trim()) return full.trim()
    const fn = String(d.first_name ?? d.given_name ?? '')
    const ln = String(d.last_name ?? d.family_name ?? '')
    const combined = `${fn} ${ln}`.trim()
    if (combined) return combined
  }
  const shortId = entry.practitioner_id.slice(0, 8)
  return `Practitioner ${shortId}…`
}

/**
 * GET /database/organizations/{org_id}/practitioners — Bearer JWT; org_id must match token org.
 */
export async function getOrganizationPractitioners(
  orgId: string,
  signal?: AbortSignal
): Promise<OrganizationPractitionerEntry[]> {
  const client = ProviderAPIClient.getInstance()
  const response = await client.get(
    `/database/organizations/${encodeURIComponent(orgId)}/practitioners`,
    undefined,
    true,
    signal
  )
  if (response.ok) {
    const data = await response.json()
    return Array.isArray(data) ? data : []
  }
  if (response.status === 401) {
    throw new Error('Not authenticated. Sign in again to load organization practitioners.')
  }
  if (response.status === 403) {
    let msg = 'Cannot access practitioners for a different organization.'
    try {
      const j = (await response.json()) as { detail?: string; message?: string }
      if (typeof j.detail === 'string') msg = j.detail
      else if (typeof j.message === 'string') msg = j.message
    } catch {
      /* keep default */
    }
    throw new Error(msg)
  }
  if (response.status === 422) {
    throw new Error('Invalid organization identifier.')
  }
  const text = await response.text().catch(() => '')
  throw new Error(text || `Failed to load practitioners (${response.status})`)
}

export function organizationPractitionerToAuthorizingSelection(entry: OrganizationPractitionerEntry): {
  authorizingProfessionalInfo: AuthorizingProfessionalInfo
  patientPhysician: PhysicianInfo
} {
  const npi = organizationPractitionerEntryNpi(entry)
  const name = organizationPractitionerDisplayName(entry)
  const d = (entry.data ?? {}) as Record<string, unknown>
  const firstName = String(d.first_name ?? d.given_name ?? name.split(/\s+/)[0] ?? '')
  const lastName = String((d.last_name ?? d.family_name ?? name.split(/\s+/).slice(1).join(' ')) || firstName)
  return {
    authorizingProfessionalInfo: {
      npi,
      name,
      email: entry.email ?? (typeof d.email === 'string' ? d.email : undefined),
    },
    patientPhysician: {
      id: entry.practitioner_id,
      name: { firstName, lastName: lastName || firstName },
      roleTitle: organizationPractitionerRoleTitle(entry),
      phone: (entry.phone ?? String(d.phone ?? '')).trim(),
      email: String(entry.email ?? d.email ?? ''),
      npi,
    },
  }
}
